import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { Badge } from './ui/badge';
import { Building2, Info } from 'lucide-react';

// Supported Banks by Country for Open Banking integrations
export const SUPPORTED_BANKS_BY_COUNTRY = {
  'NG': {
    name: 'Nigeria',
    flag: '\u{1F1F3}\u{1F1EC}',
    banks: [
      'Access Bank', 'Alat by Wema', 'Alt bank', 'Chaka', 'Cowrywise', 'Ecobank',
      'FCMB', 'Fidelity Bank', 'First Bank', 'GTBank', 'Keystone Bank', 'Kuda Bank',
      'OPay', 'Piggyvest', 'Polaris VULTe', 'Providus Bank', 'Risevest',
      'Stanbic IBTC', 'Standard Chartered', 'Sterling Bank', 'Union Bank', 'Zenith Bank'
    ]
  },
  'GH': {
    name: 'Ghana',
    flag: '\u{1F1EC}\u{1F1ED}',
    banks: [
      'Access Bank Ghana', 'Absa Bank Ghana', 'ADB Bank', 'Cal Bank', 'Ecobank Ghana',
      'Fidelity Bank Ghana', 'First Atlantic Bank', 'First National Bank', 'GCB Bank',
      'GT Bank Ghana', 'National Investment Bank', 'Prudential Bank', 'Republic Bank',
      'Societe Generale', 'Stanbic Bank Ghana', 'Standard Chartered Ghana', 'UBA Ghana',
      'United Bank for Africa', 'Zenith Bank Ghana'
    ]
  },
  'KE': {
    name: 'Kenya',
    flag: '\u{1F1F0}\u{1F1EA}',
    banks: [
      'Absa Bank Kenya', 'Bank of Africa Kenya', 'Bank of Baroda', 'Citibank Kenya',
      'Co-operative Bank', 'Diamond Trust Bank', 'Ecobank Kenya', 'Equity Bank',
      'Family Bank', 'GT Bank Kenya', 'I&M Bank', 'KCB Bank', 'M-Pesa', 'NCBA Bank',
      'Prime Bank', 'Stanbic Bank Kenya', 'Standard Chartered Kenya', 'United Bank for Africa'
    ]
  },
  'ZA': {
    name: 'South Africa',
    flag: '\u{1F1FF}\u{1F1E6}',
    banks: [
      'Absa Bank', 'African Bank', 'Bidvest Bank', 'Capitec Bank', 'Discovery Bank',
      'First National Bank (FNB)', 'First Rand Bank', 'Investec', 'Mercantile Bank',
      'Nedbank', 'Old Mutual', 'Sasfin', 'Standard Bank', 'TymeBank'
    ]
  },
  'TZ': {
    name: 'Tanzania',
    flag: '\u{1F1F9}\u{1F1FF}',
    banks: [
      'Absa Bank Tanzania', 'CRDB Bank', 'Equity Bank Tanzania', 'Exim Bank',
      'I&M Bank Tanzania', 'KCB Bank Tanzania', 'NBC Bank', 'NMB Bank',
      'Stanbic Bank Tanzania', 'Standard Chartered Tanzania'
    ]
  },
  'GB': {
    name: 'United Kingdom',
    flag: '\u{1F1EC}\u{1F1E7}',
    banks: [
      'Barclays', 'Halifax', 'HSBC', 'Lloyds Bank', 'Monzo', 'NatWest',
      'Nationwide', 'Revolut', 'Royal Bank of Scotland', 'Santander',
      'Starling Bank', 'TSB Bank', 'Virgin Money'
    ]
  },
  'US': {
    name: 'United States',
    flag: '\u{1F1FA}\u{1F1F8}',
    banks: [
      'Bank of America', 'Capital One', 'Charles Schwab', 'Chase', 'Chime',
      'Citibank', 'Citizens Bank', 'Discover', 'Fidelity', 'Fifth Third',
      'Goldman Sachs', 'HSBC USA', 'Morgan Stanley', 'Navy Federal',
      'PNC Bank', 'SoFi', 'TD Bank', 'USAA', 'US Bank', 'Wells Fargo'
    ]
  },
  'DE': {
    name: 'Germany',
    flag: '\u{1F1E9}\u{1F1EA}',
    banks: [
      'Commerzbank', 'Deutsche Bank', 'DKB', 'ING Germany', 'N26',
      'Postbank', 'Sparkasse', 'Volksbank'
    ]
  },
  'FR': {
    name: 'France',
    flag: '\u{1F1EB}\u{1F1F7}',
    banks: [
      'BNP Paribas', 'Boursorama', 'Credit Agricole', 'Credit Mutuel',
      'La Banque Postale', 'LCL', 'Societe Generale'
    ]
  },
  'NL': {
    name: 'Netherlands',
    flag: '\u{1F1F3}\u{1F1F1}',
    banks: [
      'ABN AMRO', 'Bunq', 'ING Netherlands', 'Rabobank', 'SNS Bank', 'Triodos Bank'
    ]
  }
};

// Get country display name
export const getCountryDisplayName = (countryCode) => {
  const countryNames = {
    'NG': 'Nigerian',
    'GH': 'Ghanaian',
    'KE': 'Kenyan',
    'ZA': 'South African',
    'TZ': 'Tanzanian',
    'GB': 'UK',
    'US': 'US',
    'DE': 'German',
    'FR': 'French',
    'NL': 'Dutch'
  };
  return countryNames[countryCode] || '';
};

// Get country flag
export const getCountryFlag = (countryCode) => {
  return SUPPORTED_BANKS_BY_COUNTRY[countryCode]?.flag || '\u{1F3E6}';
};

// Bank Integration Tooltip Component
export const BankIntegrationTooltip = ({ 
  countryCode, 
  children, 
  showIcon = false,
  className = '',
  maxBanksToShow = 10
}) => {
  const countryData = SUPPORTED_BANKS_BY_COUNTRY[countryCode];
  
  if (!countryData) {
    return children || null;
  }

  const banks = countryData.banks || [];
  const displayBanks = banks.slice(0, maxBanksToShow);
  const remainingCount = banks.length - maxBanksToShow;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <span className={`cursor-help inline-flex items-center gap-1 ${className}`}>
            {children}
            {showIcon && <Info className="w-3.5 h-3.5 text-muted-foreground" />}
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="bottom" 
          align="start" 
          sideOffset={12}
          collisionPadding={16}
          className="w-72 p-3 bg-card border rounded-lg shadow-lg z-50 pointer-events-none"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-lg">{countryData.flag}</span>
              <p className="text-xs font-semibold text-muted-foreground">
                Supported {countryData.name} Banks
              </p>
            </div>
            <div className="max-h-40 overflow-y-auto">
              <p className="text-xs text-foreground leading-relaxed">
                {displayBanks.join(', ')}
                {remainingCount > 0 && (
                  <span className="text-muted-foreground"> +{remainingCount} more</span>
                )}
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Bank Integration Badge - displays "Bank Integration" with tooltip
export const BankIntegrationBadge = ({ 
  countryCode, 
  variant = 'default',
  showFlag = true,
  className = '' 
}) => {
  const countryData = SUPPORTED_BANKS_BY_COUNTRY[countryCode];
  const countryName = getCountryDisplayName(countryCode);
  
  return (
    <BankIntegrationTooltip countryCode={countryCode}>
      <Badge variant={variant} className={`gap-1 ${className}`}>
        {showFlag && countryData && <span>{countryData.flag}</span>}
        {countryName ? `${countryName} Bank Integration` : 'Bank Integration'}
      </Badge>
    </BankIntegrationTooltip>
  );
};

// Compact Bank Integration Info for inline text
export const BankIntegrationInfo = ({ 
  countryCode, 
  prefix = 'Connect via',
  className = '' 
}) => {
  const countryData = SUPPORTED_BANKS_BY_COUNTRY[countryCode];
  const countryName = getCountryDisplayName(countryCode);
  
  return (
    <BankIntegrationTooltip countryCode={countryCode} showIcon>
      <span className={`text-sm text-muted-foreground ${className}`}>
        {prefix} {countryName ? `${countryName} ` : ''}Bank Integration
      </span>
    </BankIntegrationTooltip>
  );
};

export default BankIntegrationTooltip;
